/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import MailIcon from '@mui/icons-material/Mail';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Collapse, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';

import { Namespaces } from '../../../../i18n';
import { AdminOrganization } from '../../../../types/superuser/AdminOrganization';
import { AdminEmptyList } from '../AdminEmptyList/AdminEmptyList';
import { useAdminOrganizationInfoListStyles } from './AdminOrganizationInfoList.styles';
import { OrganizationProjectsInfoList } from './OrganizationProjectsInfoList/OrganizationProjectsInfoList';
import { TeamsInfoList } from './TeamsInfoList/TeamsInfoList';
import { UsersInfoList } from './UsersInfoList/UsersInfoList';

type AdminOrganizationInfoListProps = {
  rowData: AdminOrganization;
};

export function AdminOrganizationInfoList(props: AdminOrganizationInfoListProps) {
  const classes = useAdminOrganizationInfoListStyles();
  const { t } = useTranslation(Namespaces.Admin);
  const [openProjects, setOpenProjects] = useState<boolean>(false);
  const [openUsers, setOpenUsers] = useState<boolean>(false);
  const [openTeams, setOpenTeams] = useState(false);
  const [openInvitations, setOpenInvitations] = useState<boolean>(false);
  const [openRegistrationInvitations, setOpenRegistrationInvitations] = useState<boolean>(false);
  const { rowData } = props;
  const { projects: orgProjects, users, invitations, registrationInvitations, teams } = rowData;
  const projects = orgProjects ?? [];

  return (
    <List component="nav" disablePadding>
      <ListItem button onClick={() => setOpenProjects(!openProjects)}>
        <ListItemIcon>
          <ViewListIcon />
        </ListItemIcon>
        <ListItemText primary={t('organizations.info_list.fields.projects')} />
        {openProjects ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openProjects} timeout="auto" unmountOnExit>
        <List component="nav" disablePadding className={classes.nested}>
          {projects.length ? (
            projects.map(p => <OrganizationProjectsInfoList project={p} key={p.id} />)
          ) : (
            <AdminEmptyList />
          )}
        </List>
      </Collapse>
      <ListItem button onClick={() => setOpenTeams(!openTeams)}>
        <ListItemIcon>
          <GroupsIcon />
        </ListItemIcon>
        <ListItemText primary={t('organizations.info_list.fields.teams')} />
        {openTeams ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openTeams} timeout="auto" unmountOnExit>
        <List component="nav" disablePadding className={classes.nested}>
          {teams.length ? teams.map(t => <TeamsInfoList team={t} key={t.identifier} />) : <AdminEmptyList />}
        </List>
      </Collapse>
      <ListItem button onClick={() => setOpenUsers(!openUsers)}>
        <ListItemIcon>
          <GroupIcon />
        </ListItemIcon>
        <ListItemText primary={t('organizations.info_list.fields.users')} />
        {openUsers ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openUsers} timeout="auto" unmountOnExit>
        <List component="nav" disablePadding className={classes.nested}>
          {users.length ? (
            users.map(u => <UsersInfoList userDetails={u.user} role={u.role} key={u.id} />)
          ) : (
            <AdminEmptyList />
          )}
        </List>
      </Collapse>
      <ListItem button onClick={() => setOpenInvitations(!openInvitations)}>
        <ListItemIcon>
          <GroupAddIcon />
        </ListItemIcon>
        <ListItemText primary={t('organizations.info_list.fields.invitations')} />
        {openInvitations ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openInvitations} timeout="auto" unmountOnExit>
        <List component="nav" disablePadding className={classes.nested}>
          {invitations.length ? (
            invitations.map(i => <UsersInfoList userDetails={i.user} key={i.id} />)
          ) : (
            <AdminEmptyList />
          )}
        </List>
      </Collapse>
      <ListItem button onClick={() => setOpenRegistrationInvitations(!openRegistrationInvitations)}>
        <ListItemIcon>
          <PlaylistAddIcon />
        </ListItemIcon>
        <ListItemText primary={t('organizations.info_list.fields.registration_invitations')} />
        {openRegistrationInvitations ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openRegistrationInvitations} timeout="auto" unmountOnExit>
        <List component="nav" disablePadding className={classes.nested}>
          {registrationInvitations.length ? (
            registrationInvitations.map(r => (
              <ListItem key={r.id}>
                <ListItemIcon>
                  <MailIcon />
                </ListItemIcon>
                <ListItemText primary={r.email} secondary={r.createdAt} />
              </ListItem>
            ))
          ) : (
            <AdminEmptyList />
          )}
        </List>
      </Collapse>
    </List>
  );
}
